
import {first} from 'rxjs/operators';
import { Component, OnChanges, SimpleChanges } from "@angular/core";
import { RuleBuilderService } from "../../services/rule-builder.service";
import { ActivatedRoute } from "@angular/router";

import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { genId } from "../../../core/services/genId";
import { SidePanelService } from "../../../common/services/side-panel.service";

import { Button, ButtonKind, ButtonRuleType, ChangeTargetRule, FlowTemplate, Option, Page, PageType, Question, QuestionKind, Rule, RuleAction, RuleEvent, Section, State, Transition } from "../../../common/models/flow.dto";
import { convertToPlainText, deepClone, RuleEditorModel } from "../../models/editor.model";
import { LunaCodeNode } from "../../../common/models/rules.model";
import { SidePanelBase } from "../../../common/components/side-panel-base.class";

@Component({
    selector: "state-builder-rules",
    templateUrl: "./state-builder-rules.component.html",
    styleUrls: ["./state-builder-rules.component.scss"],
    standalone: false
})
export class StateBuilderRulesComponent extends SidePanelBase implements OnChanges {

    static componentName: string = "StateBuilderRulesComponent";

    public _ruleName: string = "";
    public activeButton: Button | null;
    public activePageNumber: number;
    public activeStateKey: number;
    public activeTemplate: FlowTemplate;
    public buttonError: string = "";
    public buttonPosition: string;
    public buttonRuleOptions: Array<Option> = new Array<Option>();
    public compiledExpression: LunaCodeNode | null;
    public contextTarget: string = "";
    public defaultOption: Option;
    public editActive: boolean = false;
    public editButtonMode: boolean = false;
    public existingRules: Array<RuleEditorModel> = new Array<RuleEditorModel>();
    public expression: string;
    public expressionCompiled: boolean;
    public expressionCompiles: string = "Expression compiles successfully.";
    public expressionNoCompile: string = "Expression does not compile.";
    public expressionUnevaluated: string = "Expression has not been evaluated";
    public expressionEvaluation: string;
    public isButtonRule: boolean;
    public ruleActionOptions: Array<Option> = new Array<Option>();

    get ruleName(): string {
        if (this.selectedRule) {
          if (this.selectedRule.name !== this._ruleName) {
            this._ruleName = this.selectedRule.name;
          }
        }
        return this._ruleName;
      }
    set ruleName(value: string) {
        if (this.selectedRule) {
            this.selectedRule.name = value;
        }
        this._ruleName = value;
    }
    public ruleTriggerOptions = new Array<Option>();
    public selectedAction: RuleAction | null;
    public selectedActionOption: string;
    public selectedButtonRule: ButtonRuleType | null;
    public selectedButtonRuleOption: string;
    public selectedPage: Page;
    public selectedQuestion: Question;
    public selectedRule: RuleEditorModel;
    public selectedState: State;
    public selectedTransition: Transition;
    public selectedTrigger: RuleEvent | null;
    public selectedTriggerOption: string;
    public showExistingRuleWarning: boolean = false;
    public showPanel: boolean;
    public availableSections: Section[] = [];
    public targetSection: Section;
    public triggerSection: Section;
    public availableTransitions: Transition[] = [];
    public triggerPages: Page[] = [];
    public triggerPage: Page;
    public availablePages: Page[] = [];
    public availableQuestions: Question[] = [];
    public defaultSection: Section = { id: "default", name: "...", pages: [] };
    public defaultPage: Page = { pageNumber: -1, subtitle: "...", section: "...", incomingRules: [], pageType: PageType.Input, questions: [] };
    public defaultQuestion: Question = { "key": "-1", label: "...", changeRules: [], validationRules: [], /*files: [],*/ defaultValue: "", notes: [], type: QuestionKind.Empty };
    public defaultTransition: Transition = { "key": "-1", name: "...", outgoingRules: [] };
    get disableAddRuleButton(): boolean {
        return this.disableAddRule();
    }
    public rule: RuleEditorModel;
    public isForm: boolean;

    dialog: Dialog = new Dialog("Are you sure you want to delete this rule?", [new DialogButton("Yes", true, () => this.onDialogConfirm()), new DialogButton("No", false, () => this.onDialogClose())]);

    constructor(
        public route: ActivatedRoute,
        public readonly service: RuleBuilderService,
        public sidePanelService: SidePanelService,
        public dialogService: DynamicDialogService
    ) {
        super();
    }

    ngOnInit() {
        if (this.componentData) {
            this.activeTemplate = this.componentData.activeTemplate;
            this.selectedState = this.componentData.selectedState;
            this.buttonPosition = this.componentData.buttonPosition;
            this.activeButton = this.componentData.activeButton;
            this.activePageNumber = this.componentData.activePageNumber;
            if (this.componentData.isButtonRule) {
            this.isButtonRule = this.componentData.isButtonRule;
            }
            if (this.isButtonRule && this.activeButton) {
            this.loadExistingButtonRules();
            } else {
            this.loadExistingFormRules();
            }
        }
        this.activeStateKey = parseInt(this.route.snapshot.params.stateKey, 10);
        this.defaultOption = { id: "-1", text: "..." };
        this.resetForm();
        this.loadAllActionOptions();
        this.loadButtonRuleOptions();
        this.loadTriggerOptions();
        this.loadTargets();
        this.dialogService.updateDialog(this.dialog);   
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["activeButton"]) {
            this.loadExistingButtonRules();
        }
    }

    public addButtonRule(e: Event): void {
        const newRule = deepClone(this.selectedRule);
        const rule = this.buildRule(newRule);
        if (this.selectedRule) {
            this.deleteButtonRule(this.selectedRule, false);
        }
        if (this.selectedRule && this.selectedRule.ruleId === rule.ruleId && this.selectedRule.buttonRuleType !== this.selectedButtonRule) { //not sure if this if block is really necessary any more
            switch (this.selectedRule.buttonRuleType) {
            case ButtonRuleType.Visible:
                this.activeButton.visibleRule = null;
                break;
            case ButtonRuleType.Enabled:
                this.activeButton.enabledRule = null;
                break;
            case ButtonRuleType.NotifyMessage:
                this.activeButton.notifyMessageRule = null;
                break;
            case ButtonRuleType.RedirectTo:
                this.activeButton.redirectToRule = null;
                break;
            case ButtonRuleType.Target:
                switch (this.activeButton.type) {
                case ButtonKind.JumpToPage:
                    this.activeButton.targetPageRule = null;
                    break;
                case ButtonKind.JumpToSection:
                    this.activeButton.targetSectionRule = null;
                    break;
                case ButtonKind.Transition:
                    this.activeButton.targetTransitionRule = null;
                    break;
                default:
                    break;
                }
                break;
            default:
                break;
            }
        }

        switch (this.selectedButtonRule) {
            case ButtonRuleType.Visible:
                if (!rule.name || rule.name === "") {
                    rule.name = "Button Visible Rule";
                }
                this.activeButton.visibleRule = rule;
            break;
            case ButtonRuleType.Enabled:
                if (!rule.name || rule.name === "") {
                    rule.name = "Button Enabled Rule";
                }
                this.activeButton.enabledRule = rule;
            break;
            case ButtonRuleType.NotifyMessage:
                if (!rule.name || rule.name === "") {
                    rule.name = "Button Notify Rule";
                }
                this.activeButton.notifyMessageRule = rule;
            break;
            case ButtonRuleType.RedirectTo:
                if (!rule.name || rule.name === "") {
                    rule.name = "Button Redirect to Rule";
                }
                this.activeButton.redirectToRule = rule;
            break;
            case ButtonRuleType.Target:
            switch (this.activeButton.type) {
                case ButtonKind.JumpToPage:
                    if (!rule.name || rule.name === "") {
                        rule.name = "Button Page Rule";
                    }
                    this.activeButton.targetPageRule = rule;
                break;
                case ButtonKind.JumpToSection:
                    if (!rule.name || rule.name === "") {
                        rule.name = "Button Section Rule";
                    }
                    this.activeButton.targetSectionRule = rule;
                break;
                    case ButtonKind.Transition:
                    if (!rule.name || rule.name === "") {
                        rule.name = "Button Transition Rule";
                    }
                    this.activeButton.targetTransitionRule = rule;
                break;
                case ButtonKind.Return:
                    if (!rule.name || rule.name === "") {
                        rule.name = "Button Return Rule";
                    }
                    this.activeButton.targetTransitionRule = rule;
                break;
                default:
                    console.log(`somehow trying to add a Target button rule to a button of type ${this.activeButton.type}`);
                return;
            }
            break;
            default:
                throw new Error("can't create a rule of type None");
            break;
        }

        this.updateRulesCollection(rule, null, null, this.selectedButtonRule, this.activeButton);
        this.resetForm();
    }

    public addRule(e: Event): void {
        let rule: Rule;
        let existing: Rule;
        let changeRule: ChangeTargetRule;
        let source: Transition | Page | Question | null;
        const newRule = deepClone(this.selectedRule);

        if (this.selectedRule) { //if editing, delete the existing rule so that we don't end up with two versions of the rule if the type of rule was changed
            if (this.isButtonRule || this.selectedButtonRule !== null) {
                this.deleteButtonRule(this.selectedRule, false);
            } else {
                this.deleteFormRule(this.selectedRule, false);
            }
        }

        switch (this.selectedTrigger) {
            case RuleEvent.OnInputChange:
                if (!this.selectedQuestion) {
                    throw new Error("no question selected!");
                }
                source = this.selectedQuestion;
                if (this.selectedQuestion === this.defaultQuestion) {
                    throw new Error("Question is set to default!");
                }
                if (this.selectedAction === RuleAction.UpdateContext) {
                    changeRule = this.buildChangeTargetRule(newRule);
                    changeRule.contextTarget = this.contextTarget;
                    existing = this.selectedQuestion.changeRules.find(q => q.ruleId === changeRule.ruleId) as ChangeTargetRule;
                    if (existing !== undefined && existing !== null) {
                        this.selectedQuestion.changeRules = this.selectedQuestion.changeRules.filter(r => r.ruleId !== changeRule.ruleId);
                    }
                    this.selectedQuestion.changeRules.push(changeRule);
                } else {
                    rule = this.buildRule(newRule);
                    if (this.selectedAction === RuleAction.ShowQuestion) {
                        this.selectedQuestion.visibleRule = rule;
                    } else if (this.selectedAction === RuleAction.EnableQuestion) {
                        this.selectedQuestion.enabledRule = rule;
                    }
                }
            break;
            case RuleEvent.OnPageEnter:
                if (!this.triggerPage) {
                    throw new Error("no page selected");
                }
                if (this.triggerPage === this.defaultPage) {
                    throw new Error("Page is set to default");
                }
                source = this.triggerPage;
                changeRule = this.buildChangeTargetRule(newRule);
                changeRule.contextTarget = this.contextTarget;
                existing = this.triggerPage.incomingRules.find(q => q.ruleId === changeRule.ruleId);
                if (existing) {
                    this.triggerPage.incomingRules = this.triggerPage.incomingRules.filter(r => r.ruleId !== changeRule.ruleId);
                }
                this.triggerPage.incomingRules.push(changeRule);
            break;
            case RuleEvent.OnFormComplete:
                if (!this.selectedTransition) {
                    throw new Error("no transition selected");
                }
                source = this.selectedTransition;
                changeRule = this.buildChangeTargetRule(newRule);
                changeRule.contextTarget = this.contextTarget;
                existing = this.selectedTransition.outgoingRules.find(r => r.ruleId === changeRule.ruleId);
                if (existing !== undefined && existing !== null) {
                    this.selectedTransition.outgoingRules = this.selectedTransition.outgoingRules.filter(r => r.ruleId !== changeRule.ruleId);
                }
                this.selectedTransition.outgoingRules.push(changeRule);
            break;
            default:
                source = null;
            break;
        }



        if (rule && source) {
            this.updateRulesCollection(rule, this.selectedTrigger, this.selectedAction, null, source, true);
        }

        if (changeRule && source) {
            this.updateRulesCollection(changeRule, this.selectedTrigger, this.selectedAction, null, source, true);
        }

        this.resetForm();
    }

    public buildChangeTargetRule(newRule: RuleEditorModel | null): ChangeTargetRule {
        if (!this.selectedQuestion) {
            throw new Error("no target selected");
        }

        let ruleId = genId();
        if (newRule && newRule.ruleId !== undefined && newRule.ruleId !== null && newRule.ruleId.length > 0) {
            ruleId = newRule.ruleId;
        }

        let contextExtension = "";

        if (this.selectedAction === 0)
            contextExtension = ".enabled";
        else if (this.selectedAction === 2)
            contextExtension = ".visible";

        const rv = {
            ruleId: "",
            name: (this.ruleName) ? this.ruleName : newRule.name,
            expression: (this.expression) ? this.expression : newRule.expression,
            parseTree: (this.compiledExpression) ? this.compiledExpression: newRule.expression,
            contextTarget: `${this.selectedQuestion.contextTarget}${contextExtension}`
        } as ChangeTargetRule;

        rv.ruleId = ruleId;
        return rv;
    }

    public buildRule(newRule: RuleEditorModel | null): Rule {
        let ruleId = genId();
        if (newRule && newRule.ruleId !== undefined && newRule.ruleId !== null && newRule.ruleId.length > 0) {
            ruleId = newRule.ruleId;
        }

        const rv = {
            ruleId: "",
            name: (this.ruleName) ? this.ruleName : newRule.name,
            expression: this.expression,
            parseTree: this.compiledExpression
        } as Rule;
        rv.ruleId = ruleId;
        return rv;
    }

    public changeAction(id: number): void {
        this.selectedAction = id;
        this.targetSection = this.availableSections[0];
        this.availablePages = [deepClone(this.defaultPage)];
        this.selectedPage = this.availablePages[0];
        this.availableQuestions = [deepClone(this.defaultQuestion)];
        this.selectedQuestion = this.availableQuestions[0];
    }

    public changeTrigger(id: number): void {
        this.selectedTrigger = id;

        let restrictValidRuleActions = false;
        switch (this.selectedTrigger) {
            case RuleEvent.OnPageEnter:
            {
                this.triggerSection = this.availableSections[0];
                const initialPage = deepClone(this.defaultPage);
                this.availablePages = [initialPage];
                this.triggerPage = initialPage;
                restrictValidRuleActions = true;
                break;
            }
            case RuleEvent.OnFormComplete:
            {
                this.selectedTransition = deepClone(this.defaultTransition);
                restrictValidRuleActions = true;
                break;
            }
        }


        //now change the list of avaiable actions
        if (restrictValidRuleActions) {
            this.ruleActionOptions = new Array<Option>();
            this.ruleActionOptions.push(this.defaultOption);
            this.ruleActionOptions.push(this.createActionOption(RuleAction.UpdateContext));
        }
        else {
            this.loadAllActionOptions();
        }
    }

    public closeClick(e: Event): void {
        this.resetForm();
        this.sidePanelService.toggleSidePanel(false);
    }

    public deleteButtonRule(rule: RuleEditorModel, resetFields: boolean): void {
        if (rule.sourceButton && rule.sourceButton.enabledRule) {
            if (rule.sourceButton.enabledRule.ruleId == rule.ruleId) {
                rule.sourceButton.enabledRule = null;
            }
        };

        if (rule.sourceButton && rule.sourceButton.targetPageRule) {
            if (rule.sourceButton.targetPageRule.ruleId == rule.ruleId) {
                rule.sourceButton.targetPageRule = null;
            }
        };

        if (rule.sourceButton && rule.sourceButton.targetTransitionRule) {
            if (rule.sourceButton.targetTransitionRule.ruleId == rule.ruleId) {
                rule.sourceButton.targetTransitionRule = null;
            }
        };

        if (rule.sourceButton && rule.sourceButton.visibleRule) {
            if (rule.sourceButton.visibleRule.ruleId == rule.ruleId) {
                rule.sourceButton.visibleRule = null;
            }
        };

        if (rule.sourceButton && rule.sourceButton.notifyMessageRule) {
            if (rule.sourceButton.notifyMessageRule.ruleId == rule.ruleId) {
                rule.sourceButton.notifyMessageRule = null;
            }
        };

        if (rule.sourceButton && rule.sourceButton.redirectToRule) {
            if (rule.sourceButton.redirectToRule.ruleId == rule.ruleId) {
                rule.sourceButton.redirectToRule = null;
            }
        };

        this.loadExistingButtonRules();

        if (resetFields) {
            //hide currenty editing (for now, could not get row highlight to stay)
            this.hideEditableFields();
        }
    }

    public confirmDelete(rule: RuleEditorModel, isForm: boolean): void {
        this.rule = rule;
        this.isForm = isForm;
        this.dialog.message = `Are you sure you want to delete the rule ${rule.name}?`;
        this.dialog.toggleDialog(true);
        this.dialogService.updateDialog(this.dialog);        
    }

    public deleteFormRule(rule: RuleEditorModel, resetFields: boolean): void {
        switch (rule.trigger) {
        case RuleEvent.OnInputChange:
            switch (rule.action) {
                case RuleAction.EnableQuestion:
                    rule.sourceQuestion.enabledRule = null;
                    break;
                case RuleAction.UpdateContext:
                    rule.sourceQuestion.changeRules = rule.sourceQuestion.changeRules.filter(r => r.ruleId !== rule.ruleId);
                    break;
                case RuleAction.ShowQuestion:
                    rule.sourceQuestion.visibleRule = null;
                    break;
                }
            break;
        case RuleEvent.OnPageEnter:
            rule.sourcePage.incomingRules = rule.sourcePage.incomingRules.filter(r => r.ruleId !== rule.ruleId);
            break;
        case RuleEvent.OnFormComplete:
            rule.sourceTransition.outgoingRules = rule.sourceTransition.outgoingRules.filter(r => r.ruleId !== rule.ruleId);
            break;

        }

        this.loadExistingFormRules();

        if (resetFields) {
            //hide currenty editing (for now, could not get row highlight to stay)
            this.hideEditableFields();
        }
    }

    public editButtonRule(selected: RuleEditorModel): void {
        this.startButtonRule();
        this.selectedRule = selected;
        this.selectedButtonRule = selected.buttonRuleType;
        this.selectedButtonRuleOption = this.selectedButtonRule.toString();
        this.ruleName = selected.name;
        this.expression = selected.expression;
        this.expressionCompiled = (selected.parseTree !== undefined && selected.parseTree !== null);

        if (this.expressionCompiled) {
            this.compiledExpression = selected.parseTree;
            this.expressionEvaluation = this.expressionCompiles;
        } else {
            this.expressionEvaluation = this.expressionUnevaluated;
        }
    }

    public editRule(selected: RuleEditorModel): void {
        this.editActive = true;
        this.selectedRule = selected;
        this.ruleName = selected.name;
        this.selectedButtonRule = null;
        this.contextTarget = selected.contextTarget;
        this.updateTrigger(selected.trigger.toString());
        this.updateAction(selected.action.toString());
        switch (selected.trigger) {
            case RuleEvent.OnInputChange:
                this.selectedQuestion = selected.sourceQuestion;
                this.setSectionPageByQuestion(selected.sourceQuestion);
            break;
            case RuleEvent.OnPageEnter:
                this.triggerPage = selected.sourcePage;
                this.setSectionByPage(this.triggerPage);
            break;
            case RuleEvent.OnFormComplete:
                this.selectedTransition = selected.sourceTransition;
            break;
        }
        this.expression = selected.expression;
        this.expressionCompiled = (selected.parseTree !== undefined && selected.parseTree !== null);
        if (this.expressionCompiled) {
            this.compiledExpression = selected.parseTree;
            this.expressionEvaluation = this.expressionCompiles;
        } else {
            this.expressionEvaluation = this.expressionUnevaluated;
        }
    }

    public expressionChanged(e: Event): void {
        if (this.compiledExpression) {
            this.compiledExpression = null;
            this.expressionCompiled = false;
            this.expressionEvaluation = this.expressionUnevaluated;
        }
    }

    public setSectionPageByQuestion(questionToFind: Question): void {
        let questionFound: boolean = false;
        for (let s = 0; s < this.availableSections.length; s++) {
                if (questionFound) break;
                    const sect = this.availableSections[s];
                for (let p = 0; p < sect.pages.length; p++) {
                if (questionFound) break;
                const page = sect.pages[p];
                for (let r = 0; r < page.questions.length; r++) {
                    if (questionFound) break;
                    const row = page.questions[r];
                    for (let q = 0; q < row.length; q++) {
                        if (questionFound) break;
                        if (row[q].key === questionToFind.key) {
                            this.changeTargetSection(sect);
                            this.changeTargetPage(page);
                            this.selectedPage = page;
                            this.targetSection = sect;
                            questionFound = true;
                        }
                    }
                }
            }
        }
    }

    public setSectionByPage(pageToFind : Page): void {
        let pageFound: boolean = false;
        for (let s = 0; s < this.availableSections.length; s++) {
            if (pageFound) break;
            const sect = this.availableSections[s];
            for (let p = 0; p < sect.pages.length; p++) {
                if (pageFound) break;
                const page = sect.pages[p];
                if (pageToFind.pageNumber === page.pageNumber) {
                    this.changeTargetSection(sect);
                    this.triggerSection = sect;
                    pageFound = true;
                }
            }
        }
    }

    public loadAllActionOptions(): void {
        this.ruleActionOptions = new Array<Option>();
        this.ruleActionOptions.push(this.defaultOption);
        let i = 0;
        let createdOption = this.createActionOption(i);
        while (createdOption !== undefined) {
            this.ruleActionOptions.push(createdOption);
            i++;
            createdOption = this.createActionOption(i);
        }
    }

    public createActionOption(ruleAction: number): Option {
        let rawText = RuleAction[ruleAction];
        let text = "";
        while (rawText !== undefined && rawText !== null && rawText !== "") {
            for (let c = 0; c < rawText.length; c++) {
                if (rawText.substr(c, 1) === rawText.substr(c, 1).toUpperCase()) {
                    text = text.concat(" ");
                    text = text.concat(rawText.substr(c, 1));
                } else {
                    text = text.concat(rawText.substr(c, 1));
                }
            }
            let createdOption = { id: ruleAction.toString(), text: text };
            return createdOption;
        }
    }

    public loadButtonRuleOptions(): void {
        if (this.buttonRuleOptions.length !== 0) {
            this.buttonRuleOptions = new Array<Option>();
        }
        this.buttonRuleOptions.push(this.defaultOption);
        let i = 0;
        let rawText = ButtonRuleType[i];
        let text = "";
        while (rawText !== undefined && rawText !== null && rawText !== "") {
            for (let c = 0; c < rawText.length; c++) {
                if (rawText.substr(c, 1) === rawText.substr(c, 1).toUpperCase()) {
                    text = text.concat(" ");
                    text = text.concat(rawText.substr(c, 1));
                } else {
                    text = text.concat(rawText.substr(c, 1));
                }
            }
            this.buttonRuleOptions.push({ id: i.toString(), text: text });
            i = i + 1;
            rawText = ButtonRuleType[i];
            text = "";
        }
    }

    public loadExistingButtonRules(): void {
        this.showExistingRuleWarning = false;
        if (this.existingRules === undefined || this.existingRules === null || this.existingRules.length > 0) {
            this.existingRules = new Array<RuleEditorModel>();
        }
        if (this.activeButton === undefined || this.activeButton === null) {
            return;
        }
        if (this.activeButton.visibleRule) {
            this.updateRulesCollection(this.activeButton.visibleRule, null, null, ButtonRuleType.Visible, this.activeButton);
        }
        if (this.activeButton.enabledRule) {
            this.updateRulesCollection(this.activeButton.enabledRule, null, null, ButtonRuleType.Enabled, this.activeButton);
        }
        if (this.activeButton.notifyMessageRule) {
            this.updateRulesCollection(this.activeButton.notifyMessageRule,
            null,
            null,
            ButtonRuleType.NotifyMessage,
            this.activeButton);
        }
        if (this.activeButton.redirectToRule) {
            this.updateRulesCollection(this.activeButton.redirectToRule,
            null,
            null,
            ButtonRuleType.RedirectTo,
            this.activeButton);
        }
        if (this.activeButton.targetPageRule) {
            this.updateRulesCollection(this.activeButton.targetPageRule,
            null,
            null,
            ButtonRuleType.Target,
            this.activeButton);
        }
        if (this.activeButton.targetSectionRule) {
            this.updateRulesCollection(this.activeButton.targetSectionRule,
            null,
            null,
            ButtonRuleType.Target,
            this.activeButton);
        }
        if (this.activeButton.targetTransitionRule) {
            this.updateRulesCollection(this.activeButton.targetTransitionRule,
            null,
            null,
            ButtonRuleType.Target,
            this.activeButton);
        }
    }

    public loadExistingFormRules(): void {

        this.showExistingRuleWarning = false;
        if (this.activeTemplate === undefined || this.activeTemplate === null) return;
        if (this.existingRules === undefined || this.existingRules === null || this.existingRules.length > 0) {
            this.existingRules = new Array<RuleEditorModel>();
        }
        const state = this.selectedState;
        state.transitions.forEach(t => {
            t.outgoingRules.forEach(outRule => {
            this.updateRulesCollection(outRule, RuleEvent.OnFormComplete, RuleAction.UpdateContext, null, t);
            });
        });

        for (let s = 0; s < state.sections.length; s++) {
            const sect = state.sections[s];
            for (let p = 0; p < sect.pages.length; p++) {
                const page = sect.pages[p];
                page.incomingRules.forEach(incRule => {
                    this.updateRulesCollection(incRule, RuleEvent.OnPageEnter, RuleAction.UpdateContext, null, page);
                });
                for (let r = 0; r < page.questions.length; r++) {
                    const row = page.questions[r];
                    for (let q = 0; q < row.length; q++) {
                        const question = row[q];
                        if (question.visibleRule !== undefined && question.visibleRule !== null) {
                            this.updateRulesCollection(question.visibleRule,
                            RuleEvent.OnInputChange,
                            RuleAction.ShowQuestion,
                            null,
                            question);
                        }
                        if (question.enabledRule !== undefined && question.enabledRule !== null) {
                            this.updateRulesCollection(question.enabledRule,
                            RuleEvent.OnInputChange,
                            RuleAction.EnableQuestion,
                            null,
                            question);
                        }
                        if (question.changeRules !== undefined) {
                            question.changeRules.forEach(ch => {
                                    this.updateRulesCollection(ch, RuleEvent.OnInputChange, RuleAction.UpdateContext, null, question);
                                });
                            }
                        }
                    }
            }
        }

        this.sortRulesCollection();
    }

    public loadTargets(): void {
        if (!this.activeTemplate) return;

        this.availableTransitions.push(deepClone(this.defaultTransition));
        this.availableQuestions.push(deepClone(this.defaultQuestion));
        this.availablePages.push(deepClone(this.defaultPage));

        if (!this.selectedState && this.activeStateKey) {
            this.selectedState = this.activeTemplate.states.find(s => s.key === this.activeStateKey);
        }
        if (!this.selectedState) {
            throw new Error("No State selected!");
        }
        this.availableSections.push(deepClone(this.defaultSection));
        this.targetSection = this.availableSections[0];
        for (let s = 0; s < this.selectedState.sections.length; s++) {
            this.availableSections.push(this.selectedState.sections[s]);
        }

        const state = this.selectedState;
        if (!state) return;
        const outgoing = state.transitions.filter(t => t.toStateKey !== state.key); //only want outgoing transitions!
        for (let t = 0; t < outgoing.length; t++) {
            this.availableTransitions.push(outgoing[t]);
        }
        this.selectedTransition = this.availableTransitions[0];
    }

    public loadTriggerOptions(): void {
        this.ruleTriggerOptions.push(this.defaultOption);
        let i = 0;
        let rawText = RuleEvent[i];
        let text = "";
        while (rawText !== undefined && rawText !== null && rawText !== "") {
            for (let c = 0; c < rawText.length; c++) {
            if (rawText.substr(c, 1) === rawText.substr(c, 1).toUpperCase()) {
                text = text.concat(" ");
                text = text.concat(rawText.substr(c, 1));
            } else {
                text = text.concat(rawText.substr(c, 1));
            }
            }
            this.ruleTriggerOptions.push({ id: i.toString(), text: text });
            i = i + 1;
            rawText = RuleEvent[i];
            text = "";
        }
    }

    public hideEditableFields(): void {
//close "Editing Rule: ..." section
this.editActive = false;
//reset fields
this.resetEditableFields();
    }

    public resetEditableFields(): void {
        this.showExistingRuleWarning = false;
        this.expressionEvaluation = this.expressionUnevaluated;
        this.contextTarget = "";
        this.expression = "";
        this.ruleName = "";
    }

    public resetForm(): void {
        this.loadButtonRuleOptions();
        this.resetEditableFields();

        this.selectedActionOption = this.defaultOption.id;
        this.selectedTriggerOption = this.defaultOption.id;
        this.selectedButtonRuleOption = this.defaultOption.id;
        this.editActive = false;
        this.selectedButtonRule = null;
        this.selectedAction = null;
        this.selectedTrigger = null;
        this.selectedPage = null;
        this.selectedTransition = null;
        this.selectedQuestion = null;
        this.selectedRule = null;
        this.triggerSection = null;
        this.triggerPage = null;
        this.targetSection = null;
        this.availablePages = [];
        this.availableQuestions = [];
    }

    public startButtonRule(): void {
        this.editActive = true;
        this.selectedButtonRuleOption = this.defaultOption.id;
        this.selectedButtonRule = null;
        if (this.activeButton) {
            this.editButtonMode = true;
            if (this.activeButton.type === ButtonKind.Back || this.activeButton.type === ButtonKind.Next) {
                const newOptions = new Array<Option>();
                this.buttonRuleOptions.forEach(b => {
                    if (b.id !== ButtonRuleType.Target.toString()) {
                    newOptions.push(b);
                    }
                });
                this.buttonRuleOptions = newOptions;
            }
        } else {
            this.buttonError = `Unable to identify button by position ${this.buttonPosition}`;
        }
    }

    public startFormRule(): void {
        this.resetForm();
        this.editActive = true;
    }

    public testExpression(e: Event): void {
        const exprService = this.service.compileExpression(this.expression);
        exprService.pipe(first()).subscribe((expResult) => {
            this.compiledExpression = expResult;
            this.expressionEvaluation = this.expressionCompiles;
            this.expressionCompiled = true;
        },
        (error) => {
            console.error(error);
            this.compiledExpression = null;
            this.expressionEvaluation = this.expressionNoCompile;
        });
    }

    public updateAction(e: string): void {
        this.selectedActionOption = e;
        const value = parseInt(e, 10);
        this.changeAction(value);
    }

    public updateButtonEffect(e: string): void {
        this.selectedButtonRuleOption = e;
        const value = parseInt(e, 10);
        const existing = this.existingRules.find(r => r.buttonRuleType === value);

        if (existing !== undefined && existing !== null) {
            this.showExistingRuleWarning = true;
            this.editButtonRule(existing);
        } else {
            this.selectedButtonRule = value;
        }
    }

    public updateRulesCollection(rule: Rule, trigger: RuleEvent | null, action: RuleAction | null, isButtonRule: ButtonRuleType | null, source: Transition | Page | Question | Button, sort: boolean = false): void {
        let updated = false;
        let ruleModel: RuleEditorModel = null;
        for (let i = 0; i < this.existingRules.length; i++) {
            if (this.existingRules[i].ruleId === rule.ruleId) {
                ruleModel = new RuleEditorModel(rule, action, trigger, isButtonRule);
                ruleModel.setSource(source);
                this.existingRules[i] = ruleModel;
                updated = true;
            }
            if (updated) break;
        }
        if (!updated) {
            ruleModel = new RuleEditorModel(rule, action, trigger, isButtonRule);
            ruleModel.setSource(source);
            this.existingRules.push(ruleModel);            
        }
        if (sort) {
            this.sortRulesCollection();
        }
    }

    sortRulesCollection(): void {
        this.existingRules = this.existingRules.sort((a, b) => {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
        });
    }

    public updateTrigger(e: string): void {
        this.selectedTriggerOption = e;
        const value = parseInt(e, 10);
        this.changeTrigger(value);
    }

    public getSectionTitle(index: number, section: Section): string {
        if (section.id === this.defaultSection.id) return section.name;
        return (section.name) ? `Section: ${section.name}` : `Section: ${index}`;
    }

    public changeTargetSection(section: Section): void {
        this.availablePages = [];
        this.availablePages.push(deepClone(this.defaultPage));
        for (let p = 0; p < section.pages.length; p++) {
            this.availablePages.push(section.pages[p]);
        }
    }

    public getPageTitle(page: Page): string {
        if (page.pageNumber === this.defaultPage.pageNumber) return page.subtitle;
        return (page.subtitle) ? `Page ${page.pageNumber} - ${page.subtitle}` : `Page ${page.pageNumber}`;
    }

    public changeTargetPage(page: Page): void {
        this.availableQuestions = [];
        this.availableQuestions.push(deepClone(this.defaultQuestion));
        for (let r = 0; r < page.questions.length; r++) {
            const row = page.questions[r];
            for (let q = 0; q < row.length; q++) {
                this.availableQuestions.push(row[q]);
            }
        }
    }

    public getQuestionLabel(question: Question): string {
        let text = (question.type && question.type !== QuestionKind.Empty) ? `${question.type}` : "";
        if (question.label) {
            const plainText = convertToPlainText(question.label);
            const limit = 40;
            if (plainText.length > limit) {
                text = (text.length > 0) ? `${text} - ${plainText.substr(0, limit)}` : `${plainText.substr(0, limit)}`;
            } else {
                text = (text.length > 0) ? `${text} - ${plainText}` : `${plainText}`;
            }

        }
        return text;
    }

    public disableAddRule(): boolean {
        return !this.expressionCompiled ||
            (this.selectedTrigger === RuleEvent.OnInputChange && this.selectedQuestion.key === this.defaultQuestion.key) ||
            (this.selectedTrigger === RuleEvent.OnPageEnter && this.selectedPage.pageNumber === this.defaultPage.pageNumber) ||
            (this.selectedTrigger === RuleEvent.OnFormComplete && this.selectedTransition.key === this.defaultTransition.key);
    }

    public onDialogConfirm(): void {
        if (this.isForm) {
            this.deleteFormRule(this.rule, true);
        } else {
            this.deleteButtonRule(this.rule, true);
        }
    }

    public onDialogClose(): void {
    }
}
